import { useRef, useState } from "react";
import api, { BACKEND_URL, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, X, Image as ImageIcon, ArrowUp, ArrowDown } from "@phosphor-icons/react";

/**
 * Builds a public file URL from a file id returned by /api/uploads.
 * Files with public purposes are served at /api/files/{id} without auth.
 */
export function fileUrl(idOrUrl) {
  if (!idOrUrl) return "";
  // Already a full URL? leave it alone.
  if (idOrUrl.startsWith("http://") || idOrUrl.startsWith("https://")) return idOrUrl;
  return `${BACKEND_URL}/api/files/${idOrUrl}`;
}

async function uploadImage(file, purpose) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("purpose", purpose);
  const { data } = await api.post("/uploads", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return `${BACKEND_URL}/api/files/${data.id}`;
}

export function ImageUploadField({ value, onChange, purpose = "inventory_image", testId = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pasted, setPasted] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image file.");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file, purpose);
      onChange(url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const usePastedUrl = () => {
    if (!pasted.trim()) return;
    onChange(pasted.trim());
    setPasted("");
  };

  return (
    <div data-testid={testId}>
      {value ? (
        <div className="relative w-full aspect-video bg-[#0A0A0A] border border-[#262626] overflow-hidden mb-2">
          <img src={value} alt="" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 bg-[#EF4444] text-white p-1.5"
            data-testid={`${testId}-remove`}
            aria-label="Remove"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full aspect-video bg-[#0A0A0A] border border-dashed border-[#262626] hover:border-[#00D4FF] flex flex-col items-center justify-center gap-2 mb-2 transition-colors"
          data-testid={`${testId}-trigger`}
        >
          {uploading ? (
            <span className="font-mono text-xs text-[#00D4FF]">UPLOADING…</span>
          ) : (
            <>
              <UploadSimple size={28} weight="duotone" className="text-[#00D4FF]" />
              <span className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase">CLICK TO UPLOAD</span>
              <span className="font-mono text-[10px] text-[#52525B]">JPG / PNG / WEBP</span>
            </>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Or paste image URL"
          value={pasted}
          onChange={(e) => setPasted(e.target.value)}
          className="input-rugged text-xs py-2 flex-1"
          data-testid={`${testId}-url-input`}
        />
        <button type="button" onClick={usePastedUrl} className="btn-secondary text-xs py-2 px-3" data-testid={`${testId}-url-use`}>
          USE
        </button>
      </div>
    </div>
  );
}

export function ImageGalleryField({ value = [], onChange, purpose = "inventory_image", testId = "image-gallery" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pasted, setPasted] = useState("");

  const images = Array.isArray(value) ? value : [];

  const addUrl = (url) => onChange([...images, url]);
  const removeAt = (idx) => onChange(images.filter((_, i) => i !== idx));
  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = images.slice();
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };
  const moveDown = (idx) => {
    if (idx === images.length - 1) return;
    const next = images.slice();
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    onChange(next);
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      try {
        uploaded.push(await uploadImage(file, purpose));
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
    if (uploaded.length) {
      onChange([...images, ...uploaded]);
      toast.success(`Uploaded ${uploaded.length} image${uploaded.length > 1 ? "s" : ""}`);
    }
    setUploading(false);
  };

  const usePastedUrl = () => {
    if (!pasted.trim()) return;
    addUrl(pasted.trim());
    setPasted("");
  };

  return (
    <div data-testid={testId}>
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
          {images.map((url, idx) => (
            <div key={url + idx} className="relative aspect-video bg-[#0A0A0A] border border-[#262626] overflow-hidden group" data-testid={`${testId}-item-${idx}`}>
              <img src={url} alt="" className="w-full h-full object-cover" />
              {idx === 0 && (
                <div className="absolute top-1 left-1 bg-[#00D4FF] text-black px-2 py-0.5 font-mono text-[9px] tracking-widest font-bold">
                  PRIMARY
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                <button type="button" onClick={() => moveUp(idx)} className="bg-[#262626] hover:bg-[#404040] text-white p-1.5" disabled={idx === 0} data-testid={`${testId}-up-${idx}`} aria-label="Move up">
                  <ArrowUp size={12} weight="bold" />
                </button>
                <button type="button" onClick={() => moveDown(idx)} className="bg-[#262626] hover:bg-[#404040] text-white p-1.5" disabled={idx === images.length - 1} data-testid={`${testId}-down-${idx}`} aria-label="Move down">
                  <ArrowDown size={12} weight="bold" />
                </button>
                <button type="button" onClick={() => removeAt(idx)} className="bg-[#EF4444] hover:bg-[#DC2626] text-white p-1.5" data-testid={`${testId}-remove-${idx}`} aria-label="Remove">
                  <X size={12} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 mb-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="btn-secondary text-xs py-2 px-3 flex-1"
          data-testid={`${testId}-add-btn`}
        >
          {uploading ? "UPLOADING…" : <><UploadSimple size={14} weight="bold" /> ADD IMAGES</>}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        data-testid={`${testId}-input`}
      />
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Or paste an image URL"
          value={pasted}
          onChange={(e) => setPasted(e.target.value)}
          className="input-rugged text-xs py-2 flex-1"
          data-testid={`${testId}-url-input`}
        />
        <button type="button" onClick={usePastedUrl} className="btn-secondary text-xs py-2 px-3" data-testid={`${testId}-url-add`}>
          ADD
        </button>
      </div>
      {images.length === 0 && (
        <div className="flex items-center gap-2 mt-2 font-mono text-[10px] text-[#52525B] tracking-widest uppercase">
          <ImageIcon size={12} weight="duotone" /> First image is shown as primary on listings
        </div>
      )}
    </div>
  );
}
